import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Building2, Users, FileText, Search, Edit, Trash2, Eye, Home, LogOut, ArrowLeft, RefreshCw, AlertTriangle, Crown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Companies() {
  const { user, token, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [deleteStep, setDeleteStep] = useState(1);
  const [deleting, setDeleting] = useState(false);
  const [companyStats, setCompanyStats] = useState({});
  const [swappingMaster, setSwappingMaster] = useState(false);

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      navigate('/dashboard');
      return;
    }
    fetchCompanies();
  }, [user, navigate]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const [companiesRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/companies/`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/companies/admin/all-stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setCompanies(companiesRes.data);
      setCompanyStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const handleSwapMaster = async () => {
    if (!window.confirm('This will make "set-in-stone" the new Master Platform and demote "memorial-desk". Continue?')) {
      return;
    }
    try {
      setSwappingMaster(true);
      await axios.post(
        `${BACKEND_URL}/api/companies/admin/swap-master`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Master status swapped successfully! You may need to re-login.');
      fetchCompanies();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to swap master'); toast.error(msg);
    } finally {
      setSwappingMaster(false);
    }
  };

  const handleDeleteCompany = async () => {
    if (!selectedCompany) {
      return;
    }
    
    try {
      setDeleting(true);
      await axios.delete(`${BACKEND_URL}/api/companies/${selectedCompany.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Company "${selectedCompany.name}" deleted`);
      setShowDeleteDialog(false);
      setDeleteStep(1);
      setSelectedCompany(null);
      fetchCompanies();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to delete company'); toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  const filteredCompanies = companies.filter(c => 
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (company) => {
    if (company.is_master) {
      return <Badge className="bg-purple-600 text-white">Master Platform</Badge>;
    }
    const status = company.status || 'active';
    const variants = {
      active: 'bg-green-100 text-green-800',
      trial: 'bg-blue-100 text-blue-800',
      suspended: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return <Badge className={variants[status] || variants.active}>{status}</Badge>;
  };

  // Check if we have duplicate Set In Stone companies
  const hasDuplicateSetInStone = companies.filter(c => 
    c.name?.toLowerCase().includes('set in stone')
  ).length > 1;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
        <RefreshCw className="h-8 w-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800">
      {/* Header */}
      <header className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/dashboard')}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-bold text-white">Manage Companies</h1>
                <p className="text-xs text-slate-400">{companies.length} registered companies</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={fetchCompanies}
                className="text-slate-300 hover:text-white hover:bg-slate-700"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button variant="ghost" onClick={logout} className="text-slate-300 hover:text-white hover:bg-slate-700">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Swap Master Alert - shown when duplicates exist */}
        {hasDuplicateSetInStone && (
          <div className="mb-6 p-4 bg-amber-900/30 border border-amber-600 rounded-lg">
            <h3 className="text-amber-300 font-medium mb-2 flex items-center gap-2">
              <Crown className="h-5 w-5" />
              Swap Master Company
            </h3>
            <p className="text-sm text-slate-300 mb-3">
              Make <strong>set-in-stone</strong> the Master Platform instead of <strong>memorial-desk</strong>. 
              This will allow you to delete the old memorial-desk company.
            </p>
            <Button
              onClick={handleSwapMaster}
              disabled={swappingMaster}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {swappingMaster ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Swapping...
                </>
              ) : (
                <>
                  <Crown className="h-4 w-4 mr-2" />
                  Make set-in-stone the Master
                </>
              )}
            </Button>
          </div>
        )}

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search companies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
            />
          </div>
        </div>

        {/* Companies Grid */}
        <div className="grid gap-4">
          {filteredCompanies.map((company) => {
            const stats = companyStats[company.id] || { orders: 0, worktops: 0, users: 0 };
            const hasNoData = stats.orders === 0 && stats.worktops === 0 && stats.users === 0;
            
            return (
              <Card 
                key={company.id} 
                data-testid={`company-card-${company.id}`}
                className={`border ${company.is_master ? 'bg-purple-900/20 border-purple-700' : 'bg-slate-800/50 border-slate-700'}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Building2 className={`h-5 w-5 ${company.is_master ? 'text-purple-400' : 'text-blue-400'}`} />
                        <h3 className="text-lg font-semibold text-white">{company.name}</h3>
                        {getStatusBadge(company)}
                      </div>
                      <p className="text-sm text-slate-400 mb-2">{company.email}</p>
                      <p className="text-xs text-slate-500 font-mono">ID: {company.id}</p>
                      
                      {/* Stats */}
                      <div className="flex gap-6 mt-3 text-sm">
                        <div className="flex items-center gap-1">
                          <FileText className={`h-4 w-4 ${stats.orders > 0 ? 'text-green-400' : 'text-slate-600'}`} />
                          <span className={stats.orders > 0 ? 'text-green-400' : 'text-slate-500'}>
                            {stats.orders} orders
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <FileText className={`h-4 w-4 ${stats.worktops > 0 ? 'text-blue-400' : 'text-slate-600'}`} />
                          <span className={stats.worktops > 0 ? 'text-blue-400' : 'text-slate-500'}>
                            {stats.worktops} worktops
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Users className={`h-4 w-4 ${stats.users > 0 ? 'text-purple-400' : 'text-slate-600'}`} />
                          <span className={stats.users > 0 ? 'text-purple-400' : 'text-slate-500'}>
                            {stats.users} users
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/company-settings?company=${company.id}`)}
                        className="border-slate-600 text-slate-300 hover:bg-slate-700"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      
                      {!company.is_master && (
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`delete-company-btn-${company.id}`}
                          onClick={() => {
                            setSelectedCompany(company);
                            setDeleteStep(1);
                            setShowDeleteDialog(true);
                          }}
                          className={`border-red-600 text-red-400 hover:bg-red-900/20 ${hasNoData ? 'animate-pulse' : ''}`}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {filteredCompanies.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            No companies found matching "{searchTerm}"
          </div>
        )}
      </main>

      {/* Delete Confirmation Dialog — two-step confirm */}
      <Dialog open={showDeleteDialog} onOpenChange={(o) => { setShowDeleteDialog(o); if (!o) setDeleteStep(1); }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="h-5 w-5" />
              Delete Company
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This will permanently delete the company and ALL associated data.
            </DialogDescription>
          </DialogHeader>
          
          {selectedCompany && (
            <div className="space-y-4">
              <div className="p-4 bg-red-900/30 rounded-lg border border-red-700">
                <p className="text-lg font-bold text-white">{selectedCompany.name}</p>
                <p className="text-sm text-slate-400 font-mono">ID: {selectedCompany.id}</p>
                <div className="mt-2 text-xs text-red-400">
                  This will delete all orders, users, settings, and data for this company.
                </div>
              </div>
              {deleteStep === 2 && (
                <div className="p-3 bg-red-600/20 border border-red-500 rounded-lg text-sm text-red-300 font-semibold" data-testid="delete-final-warning">
                  Final confirmation — this cannot be undone. Click "Confirm Deletion" to permanently delete {selectedCompany.name}.
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => { setShowDeleteDialog(false); setDeleteStep(1); }}
              className="border-slate-600 text-slate-300"
              data-testid="delete-cancel-btn"
            >
              Cancel
            </Button>
            {deleteStep === 1 ? (
              <Button 
                onClick={() => setDeleteStep(2)}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="delete-are-you-sure-btn"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Yes, I'm Sure — Continue
              </Button>
            ) : (
              <Button 
                onClick={handleDeleteCompany}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 disabled:opacity-50"
                data-testid="delete-confirm-final-btn"
              >
                {deleting ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Confirm Deletion
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
